<template>
  <div class="filter-bar flex flex-col bg-surface-0 dark:bg-surface-900 md:max-w-full">
    <section class="filter-bar--filters-wrapper w-full">
      <div class="grid grid-cols-2 md:grid-cols-6 xl:grid-cols-12 gap-6 w-full">
        <div class="filter-bar--search-wrapper col-span-2 md:col-span-3 xl:col-span-2">
          <label class="block text-xs font-medium text-surface-600 dark:text-surface-300 mb-1"
            >Search</label
          >
          <InputText
            id="search"
            v-model="searchValue"
            class="w-full"
            icon="pi pi-search"
            placeholder="Search"
            size="small"
          />
        </div>

        <div class="filter-bar--owner-wrapper col-span-1 md:col-span-2">
          <label class="block text-xs font-medium text-surface-600 dark:text-surface-300 mb-1"
            >Owners</label
          >
          <MultiSelect
            id="owner"
            v-model="ownerSelected"
            :options="ownerOptions"
            optionLabel="label"
            optionValue="value"
            class="w-full"
            :showClear="true"
            :filter="true"
            placeholder="Owners"
            selectedItemsLabel="Multiple items selected"
            :maxSelectedLabels="1"
            size="small"
          />
        </div>

        <div class="filter-bar--platform-wrapper col-span-1 xl:col-span-2">
          <label class="block text-xs font-medium text-surface-600 dark:text-surface-300 mb-1"
            >Platforms</label
          >
          <MultiSelect
            id="platform"
            v-model="platformSelected"
            :options="platformOptions"
            optionLabel="label"
            optionValue="value"
            class="w-full"
            :showClear="true"
            :filter="true"
            placeholder="Platforms"
            size="small"
            :maxSelectedLabels="1"
            :showToggleAll="false"
            :showSelectAll="false"
          />
        </div>

        <div class="filter-bar--tolerance-wrapper col-span-2 md:col-span-3 xl:col-span-2">
          <label class="block text-xs font-medium text-surface-600 dark:text-surface-300 mb-1"
            >Tolerances</label
          >
          <MultiSelect
            id="tolerance"
            v-model="toleranceSelected"
            :options="toleranceOptions"
            optionLabel="label"
            optionValue="value"
            class="w-full"
            :showClear="true"
            :filter="true"
            placeholder="Tolerances"
            size="small"
            :maxSelectedLabels="1"
            :showToggleAll="false"
            :showSelectAll="false"
          />
        </div>

        <div class="filter-bar--reset-wrapper col-span-2 flex justify-end items-end gap-3 xl:col-start-11">
          <Button
            label="Reset"
            variant="outlined"
            @click="reset"
            icon="pi pi-times"
            size="small"
            data-test-id="fcc-merchants-reset-button"
          />
          <Button
            label="Refresh"
            @click="refreshMerchants"
            icon="pi pi-refresh"
            size="small"
            data-test-id="fcc-merchants-refresh-button"
          />
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, watch, ref } from 'vue'
import { storeToRefs } from 'pinia'
import MultiSelect from 'primevue/multiselect'
import InputText from 'primevue/inputtext'
import Button from 'primevue/button'
import { useFCCMerchantStore } from '@/stores/fcc'
import { useFrontegg } from '@frontegg/vue'

const { authState } = useFrontegg()
const merchantStore = useFCCMerchantStore()
const { merchants, filters } = storeToRefs(merchantStore)

const ownerOptions = computed(() => filters.value.owner.options)
const ownerSelected = computed({
  get: () => filters.value.owner.selected,
  set: (value: string[]) => {
    merchantStore.updateFilters({
      owner: { selected: value, options: filters.value.owner.options },
    })
  }
})
const platformOptions = computed(() => filters.value.platform.options)
const platformSelected = computed({
  get: () => filters.value.platform.selected,
  set: (value: string[]) => {
    merchantStore.updateFilters({
      platform: { selected: value, options: filters.value.platform.options },
    })
  }
})
const toleranceOptions = computed(() => filters.value.tolerance.options)
const toleranceSelected = computed({
  get: () => filters.value.tolerance.selected,
  set: (value: string[]) => {
    merchantStore.updateFilters({
      tolerance: { selected: value, options: filters.value.tolerance.options },
    })
  }
})

// Use ref for two-way binding with PrimeVue InputText
const searchValue = ref<string>(filters.value.search.value || '')

// Watch for changes and update store
watch(searchValue, (newValue) => {
  merchantStore.updateFilters({ search: { value: newValue } })
})

// Watch store changes to update local refs
watch(() => filters.value.search.value, (newSearch) => {
  searchValue.value = newSearch || ''
})

watch(
  merchants,
  () => {
    const owners = Array.from(
      new Set(merchants.value.map(m => m.MERCHANT_OWNER)),
    )
    const platforms = Array.from(new Set(merchants.value.map(m => m.PLATFORM)))
    const tolerances = Array.from(
      new Set(merchants.value.map(m => m.TOLERANCE_ID)),
    )

    merchantStore.updateFilters({
      owner: {
        selected: filters.value.owner.selected.filter(s => owners.includes(s)),
        options: owners.map(o => ({ value: o, label: o })),
      },
      platform: {
        selected: filters.value.platform.selected.filter(s =>
          platforms.includes(s),
        ),
        options: platforms.map(p => ({ value: p, label: p })),
      },
      tolerance: {
        selected: filters.value.tolerance.selected.filter(s =>
          tolerances.includes(s),
        ),
        options: tolerances.map(t => ({ value: t, label: t })),
      },
    })
  },
  { immediate: true },
)

function reset() {
  merchantStore.resetFilters()
  // Reset local refs
  searchValue.value = ''
}

function refreshMerchants() {
  merchantStore.fetchMerchants(authState)
}
</script>
